import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import { useAccounts } from "../settings/queries.js";
import { RefreshButton } from "../../shared/ui/refresh-button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { RecoverPositionControl } from "./recover-position-control.js";
import { ClosePositionControl } from "./close-position-control.js";
import { formatUsd } from "./models.js";
import { usePositions } from "./queries.js";

export function PositionsPage() {
  const api = useApi();
  const positions = usePositions();
  const accounts = useAccounts();
  const manualStatus = useQuery({
    queryKey: ["manual-trading-status"],
    queryFn: async ({ signal }) => requireData(await api.GET("/manual-trading/status", { signal })),
  });
  const [search, setSearch] = useSearchParams();
  const accountId = search.get("account") ?? "";
  const symbol = search.get("symbol") ?? "";
  const accountById = new Map((accounts.data ?? []).map((account) => [account.id, account]));
  const manualAvailability = manualStatus.isLoading
    ? "checking"
    : manualStatus.error !== null
      ? "unavailable"
      : manualStatus.data?.enabled === true
        ? "enabled"
        : "disabled";
  const visible = useMemo(
    () =>
      (positions.data ?? []).filter(
        (position) =>
          (accountId === "" || position.brokerAccountId === accountId) &&
          (symbol === "" || position.symbol.includes(symbol.toUpperCase())),
      ),
    [accountId, positions.data, symbol],
  );
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value === "") next.delete(key);
    else next.set(key, value);
    setSearch(next, { replace: true });
  };

  return (
    <section className="page-stack page-stack--compact" aria-label="Positions">
      <div className="page-toolbar">
        <h1>Positions</h1>
        <RefreshButton queries={[positions, accounts, manualStatus]} />
      </div>
      <div className="content-card filter-bar filter-bar--two" aria-label="Position filters">
        <div className="field-stack">
          <label htmlFor="position-account">Account</label>
          <select
            id="position-account"
            value={accountId}
            onChange={(event) => setFilter("account", event.currentTarget.value)}
          >
            <option value="">All accounts</option>
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.displayName} · {account.status}
              </option>
            ))}
          </select>
        </div>
        <div className="field-stack">
          <label htmlFor="position-symbol">Symbol</label>
          <input
            id="position-symbol"
            value={symbol}
            onChange={(event) => setFilter("symbol", event.currentTarget.value)}
          />
        </div>
      </div>
      {positions.isLoading || accounts.isLoading ? (
        <ResourceState
          kind="loading"
          title="Loading broker positions"
          description="Reading isolated position and account state from TradingEngine."
        />
      ) : null}
      {positions.error !== null ? (
        <ResourceState
          kind="error"
          title="Positions unavailable"
          description="TradingEngine could not return current position state. No local projection is shown as broker truth."
        />
      ) : null}
      {accounts.error !== null ? (
        <ResourceState
          kind="degraded"
          title="Account labels unavailable"
          description="Positions remain isolated by stable account ID, but account display names could not be loaded."
        />
      ) : null}
      {!positions.isLoading && positions.error === null && visible.length === 0 ? (
        <ResourceState
          kind="empty"
          title="No matching positions"
          description="There are no open positions for the selected account and symbol."
        />
      ) : null}
      {visible.length === 0 ? null : (
        <div className="content-card trade-table-scroll">
          <table className="trade-table position-table" aria-label="Open positions">
            <thead>
              <tr>
                {[
                  "Symbol",
                  "Account",
                  "Size",
                  "Trade Price",
                  "Market Price",
                  "PnL",
                  "Stop Price",
                  "Target Price",
                  "Status / Actions",
                ].map((label) => (
                  <th scope="col" key={label}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((position) => {
                const account = accountById.get(position.brokerAccountId);
                return (
                  <tr key={position.id}>
                    <th scope="row">
                      <h2>{position.symbol}</h2>
                    </th>
                    <td>{account?.displayName ?? position.brokerAccountId}</td>
                    <td>
                      {position.quantity}
                      <small className="position-cell-note">{position.side}</small>
                    </td>
                    <td>{formatUsd(position.averagePriceUsd)}</td>
                    <td>
                      {formatUsd(position.currentPriceUsd)}
                      {position.priceObservedAt ? (
                        <small className="position-cell-note">
                          As of {new Date(position.priceObservedAt).toLocaleString()}
                        </small>
                      ) : null}
                    </td>
                    <td>{formatUsd(position.unrealizedPnlUsd)}</td>
                    <td>
                      {formatUsd(position.stopPriceUsd)}
                      {position.levelsSource === "strategy" &&
                      position.stopPriceUsd !== undefined ? (
                        <small className="position-cell-note">Bot level</small>
                      ) : null}
                    </td>
                    <td>
                      {formatUsd(position.targetPriceUsd)}
                      {position.levelsSource === "strategy" &&
                      position.targetPriceUsd !== undefined ? (
                        <small className="position-cell-note">Bot level</small>
                      ) : null}
                    </td>
                    <td>
                      <span className={`status-pill status-pill--${position.reconciliation}`}>
                        {position.reconciliation}
                      </span>
                      <details>
                        <summary>Details &amp; actions</summary>
                        <div className={`truth-banner truth-banner--${position.source}`}>
                          <strong>
                            {position.source === "broker"
                              ? "Broker-confirmed source"
                              : "Local projection — not broker truth"}
                          </strong>
                          <span>
                            {position.stale ? "Stale observation" : "Latest observation"}:{" "}
                            {position.observedAt === ""
                              ? "unavailable"
                              : new Date(position.observedAt).toLocaleString()}
                          </span>
                        </div>
                        {position.levelsObservedAt ? (
                          <small className="position-cell-note">
                            Bot levels updated:{" "}
                            {new Date(position.levelsObservedAt).toLocaleString()}
                          </small>
                        ) : null}
                        {position.reconciliation === "discrepant" ? (
                          <ResourceState
                            kind="error"
                            title="Broker discrepancy"
                            description="TradingEngine found a difference between expected and broker position state. Live actions may be quarantined."
                          />
                        ) : null}
                        {position.reconciliation === "pending" ? (
                          <ResourceState
                            kind="stale"
                            title="Reconciliation pending"
                            description="This projection has not yet been confirmed by Tastytrade."
                          />
                        ) : null}
                        {position.reconciliation === "unavailable" ? (
                          <ResourceState
                            kind="degraded"
                            title="Reconciliation unavailable"
                            description="Broker state could not be confirmed."
                          />
                        ) : null}
                        <nav
                          className="button-row"
                          aria-label={`${position.symbol} related records`}
                        >
                          <Link
                            className="button button--quiet"
                            to={`/bots?symbol=${encodeURIComponent(position.symbol)}`}
                          >
                            Related Bots
                          </Link>
                          <Link
                            className="button button--quiet"
                            to={`/history?account=${encodeURIComponent(position.brokerAccountId)}&symbol=${encodeURIComponent(position.symbol)}`}
                          >
                            History
                          </Link>
                        </nav>
                        <RecoverPositionControl position={position} />
                        <ClosePositionControl
                          account={account}
                          availability={manualAvailability}
                          position={position}
                        />
                      </details>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
